import { useRouter } from 'next/router'
import Script from 'next/script'
import { type OrganizationJsonLdProps, NextSeo } from 'next-seo'
import { type FC, type ReactNode, useState } from 'react'

import { DEFAULT_USER_LOCATION } from '@/constants/location'
import { getSearchCategoryFromSearchQuery } from '@/domain/category/getSearchCategoryFromSearchQuery'
import { getCurrentPageFromPagination } from '@/domain/srp/getCurrentPageFromPaginationData'
import { getSrpComposedUrl } from '@/domain/srp/getSrpComposedUrl'
import { getSrpMetaData } from '@/domain/srp/seo/getSrpMetaData'
import { useGetSearchResultsData } from '@/hooks/srp/useGetSearchResultsData'
import { useLocale } from '@/hooks/useLocale'
import { type IntlResponse } from '@/types/translations'

const JSON_LD_ORGANIZATION_ID = 'srp-organization-json-ld'

export type SeoMetadataProps = {
  pageLoadId: string
  children: (searchH1: string | IntlResponse) => ReactNode
}

const ORGANIZATION_JSON_LD: OrganizationJsonLdProps = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Kijiji Canada',
  alternateName: 'Kijiji',
  url: 'https://www.kijiji.ca/',
  sameAs: [
    'https://www.facebook.com/Kijiji.ca/',
    'https://twitter.com/Kijiji',
    'https://plus.google.com/+KijijiCanada',
    'https://www.youtube.com/user/kijijicanadacorp',
    'https://www.linkedin.com/company/kijiji-canadasmarketplace',
    'https://www.wikidata.org/wiki/Q1741191',
  ],
  logo: 'https://ca.classistatic.com/static/V/12049/img/Kijiji-Canada-Logo-1.jpg',
}

export const SeoMetadata: FC<SeoMetadataProps> = ({ children, pageLoadId }) => {
  const { query } = useRouter()
  const { data: { searchQuery, controls, pagination } = {} } = useGetSearchResultsData()
  const { apiLocale } = useLocale()

  const [organizationScript] = useState<ReactNode>(
    <Script
      key={`${pageLoadId}-${JSON_LD_ORGANIZATION_ID}`}
      id={JSON_LD_ORGANIZATION_ID}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(ORGANIZATION_JSON_LD) }}
    />
  )

  const category = getSearchCategoryFromSearchQuery(searchQuery?.category)
  const location = searchQuery?.location || DEFAULT_USER_LOCATION
  const currentPage = pagination ? getCurrentPageFromPagination(pagination) : 1

  const { h1, ...seo } = getSrpMetaData({
    apiLocale,
    category: category,
    currentPage,
    filters: controls?.filtering || [],
    keyword: searchQuery?.keywords || '',
    location: location.name,
    totalListingsCount: pagination?.totalCount || 0,
    url: getSrpComposedUrl(query),
  })

  return (
    <>
      {organizationScript}

      <NextSeo
        title={seo.title}
        robotsProps={{ maxImagePreview: 'large' }}
        additionalMetaTags={[{ name: 'google', content: 'nositelinkssearchbox' }]}
        {...seo}
      />

      {children(h1)}
    </>
  )
}
